import {Configurator} from 'substance'

import {DWExposer} from './DWExposer'
import {DWKeyboardManager} from './DWKeyboardManager'
import {PluginModuleRegistry} from './registries/PluginModuleRegistry'
import {BarComponentRegistry} from './registries/BarComponentRegistry'
import {DWLabelProvider} from './DWLabelProvider'
import {ValidatorRegistry} from './registries/ValidatorRegistry'
import {HookRegistry} from './registries/HookRegistry'
import {ConflictHandlerRegistry} from './registries/ConflictHandlerRegistry'

class DWConfigurator extends Configurator {

    constructor() {
        super()

        this._configuration = null
        this._exposer = new DWExposer()
        this._packagesToImportAfter = []

        this.dwKeyboardManager = new DWKeyboardManager({
            substanceAddKeyboardShortcut: super.addKeyboardShortcut.bind(this)
        })

        this.hookRegistry = new HookRegistry()
        this.pluginModuleRegistry = new PluginModuleRegistry()
        this.validatorRegistry = new ValidatorRegistry()
        this.conflictHandlerRegistry = new ConflictHandlerRegistry()
        this.barComponentRegistry = new BarComponentRegistry({
            getLabel: (label) => this.getLabelProvider().getLabel(label)
        })

        // Legacy name mapping to new names
        this.deprecatedComponentsMap = {}
    }

    /**
     * Gets the command associated with a key combination
     * from KeyboardManager
     *
     * @param event
     * @returns {string|boolean}
     */
    getCommandForKeyDownEvent(event) {
        return this.dwKeyboardManager.getCommandForKeyDownEvent(event)
    }

    /**
     * Adds a keyboard shortcut
     *
     * @param {string} combo - String representing a keyboard shortcut, eg. 'ctrl+s', 'alt+i', 'ctrl+alt+delete'
     * @param {{command: string}} spec - Specification for the shortcut, mainly what command to run when pressed
     * @param {boolean} globalCombo
     * @param {string} description
     */
    addKeyboardShortcut(combo, spec, globalCombo, description) {
        return this.dwKeyboardManager.addKeyboardShortcut(combo, spec, globalCombo, description)
    }

    /**
     * Register converter within Substance, always supplies IDF as type
     *
     * @param {string|object} converter Converter object (or previously type, which is deprecated)
     * @param {object} deprecatedConverter Converter object, parameter is deprecated
     */
    addConverter(converter, deprecatedConverter) {
        if (typeof converter !== 'object') {
            converter = deprecatedConverter
            console.warn('configurator.addConverter(type, converter) is deprecated, use configurator.addConverter(converter) instead')
        }

        super.addConverter('idf', converter)
    }

    /**
     * Get current configured locale
     *
     * @returns {string} Current configured locale, or default value ('en_US')
     */
    getLocale() {
        const language = this.getWriterConfigValue('language')
        if (!language) {
            return 'en_US'
        }

        return language
    }

    /**
     * Set the configuration json object
     * @param {object} configuration Configuration object
     */
    setConfiguration(configuration) {
        this._configuration = configuration
    }

    /**
     * Retrieve full configuration json object
     * @return {object} Configuration object
     */
    getConfiguration() {
        return this._configuration
    }

    /**
     * Retrieve configuration value for a key in
     * the Writer client configuration
     *
     * @param {string} field
     * @param {*} defaultValue
     * @returns {*|null}
     */
    getWriterConfigValue(field, defaultValue = null) {
        if(this._configuration && this._configuration.hasOwnProperty(field)) {
            return this._configuration[field]
        }

        return defaultValue
    }

    /**
     * Expose a named object in the writer namespace to plugins
     * @param  {string} name Name of object
     * @param  {*} obj  Actual object
     */
    expose(name, obj) {
        this._exposer.expose(name, obj)
    }

    /**
     * Gets a label provider based on writer configuration
     *
     * @returns {DWLabelProvider}
     */
    getLabelProvider() {
        let labelLanguage = 'en'
        if (this.getConfiguration() && this.getConfiguration().labelLanguage) {
            labelLanguage = this.getConfiguration().labelLanguage
        }

        if (!this.labelProvider) {
            this.labelProvider = new DWLabelProvider(this.config.labels, labelLanguage)
        }

        return this.labelProvider
    }

    /**
     * Returns all registered sidebar components
     *
     * @returns {array}
     */
    getSidebarPanels() {
        return this.barComponentRegistry.getSidebarComponents()
    }

    /**
     * Returns all registered sidebar components
     *
     * @returns {array}
     */
    getTopbarComponents() {
        return this.barComponentRegistry.getTopbarComponents()
    }

    /**
     * @deprecated 2018-01-11
     */
    addComponentToSidebarTop(pluginId, component) { /* eslint-disable-line */
        console.error('Calls to configurator.addComponentToSidebarTop() are deprecated and ignored, use configurator.addToSideBar()')
    }

    /**
     * Add a substance Component in a popover that is triggered by an
     * icon in the top bar.
     *
     * @param {string} id Package id
     * @param {object} def Definition for the popover trigger icon or button
     * @param {Component} component Substance component
     *
     * @example
     * config.addPopover(
     *      'mypopover-2001',     // Unique id for this popover
     *      {
     *          icon: 'fa-plane', // Font awesome icon class or image url
     *          align: 'left'     // Left or right aligned
     *      },
     *      AboutComponent        // Component to render in popover
     * )
     */
    addPopover(id, def, component) {
        return this.barComponentRegistry.addPopoverComponent(id, def, component)
    }

    /**
     * Add a substance Component that is displayed in the top bar.
     *
     * @param {string} id Package id
     * @param {object} def Definition top bar component
     * @param {Component} component Substance component
     *
     * @example
     * config.addTopBarComponent(
     *      'mytopbarcomponent-2001',     // Unique id for this popover
     *      {
     *          align: 'left'     // Left or right aligned
     *      },
     *      AboutComponent        // Component to render in popover
     * )
     */
    addTopBarComponent(id, def, component) {
        return this.barComponentRegistry.addTopBarComponent(id, def, component)
    }

    /**
     * Adds a component with pluginConfig to a new or existing tab in the right sidebar.
     * Target tab is overridable from plugin config.
     *
     * @param {string} tabId Which tab the plugin should be placed in. Overridable in plugin's config.
     * @param {object} pluginConfig Config object for plugin
     * @param {Component} component Component class which should render when tab is selected
     *
     * @example <caption>Used in a plugins *Package.js-file</caption>
     * config.addToSidebar(
     *      'main',         // Id/Name for selected tab
     *      pluginConfig,   //
     *      AboutComponent  // Component to render in tab
     * )
     *
     * @example <caption>Tab override in config</caption>
     * {
     *     "id": "se.infomaker.genericplugin",
     *     "name": "genericplugin",
     *     "url": "http://localhost:5001/im-genericplugin.js",
     *     "style": "http://localhost:5001/im-genericplugin.css",
     *     "enabled": true,
     *     "mandatory": false,
     *     "tab": "Overriden Tab",
     *     "data": { }
     * }
     */
    addToSidebar(tabId, pluginConfig, component) {
        return this.barComponentRegistry.addToSidebar(tabId, pluginConfig, component)
    }

    /**
     * Register a plugin provided module (class) that can be used to export
     * specific plugin functionality to other plugins. If a consumerPluginId
     * is provided only that plugin will consume, or use, this module. This
     * is used for when a plugin provides well defined extension points.
     *
     * @param {string} moduleId - Provided module id
     * @param {object} module - Actual module
     * @param {object} targetId - Optional target id that identifies the consumer of this module
     */
    addPluginModule(moduleId, module, targetId = false) {
        return this.pluginModuleRegistry.addPluginModule(moduleId, module, targetId)
    }

    /**
     * Return a fresh instance of a specific plugin provided module
     *
     * @param {string} moduleId Module id
     *
     * @return {object|null}
     */
    getPluginModule(moduleId) {
        return this.pluginModuleRegistry.getPluginModule(moduleId)
    }

    /**
     * Get all modules targeted at a specific consumer
     *
     * @param {string} targetId Target id of the consumer
     */
    getPluginModulesForTarget(targetId) {
        return this.pluginModuleRegistry.getPluginModulesForTarget(targetId)
    }

    /**
     * Retrieve the configured timeout in ms when waiting for plugins to load
     * @return {int}
     */
    getPluginLoadTimeout() {
        return (this.getWriterConfigValue('pluginLoadTimeout')) || 20000
    }

    /**
     * Adds a tool component to the content menu, aka. pen icon.
     * Executes a Command.
     *
     * @param {string} commandName - Name of the Command to execute
     * @param {class} ToolClass - Class which inherits from Substance's Tool class
     */
    addContentMenuTool(commandName, ToolClass) {
        const options = {
            toolGroup: 'content-menu'
        }

        super.addTool(commandName, ToolClass, options)
    }

    /**
     * Adds a Tool to the top of the Content Menu. Executes a Command.
     *
     * @param {string} commandName - Name of the Command to execute
     * @param {class} ToolClass - Class which inherits from Substance's Tool class
     */
    addContentMenuTopTool(commandName, ToolClass) {
        const options = {
            toolGroup: 'content-top-menu'
        }

        super.addTool(commandName, ToolClass, options)
    }

    /**
     * Adds a Tool to the Context menu. Executes a Command.
     *
     * @param {string} commandName - Name of the Command to execute
     * @param {class} ToolClass - Class which inherits from Substance's Tool class
     */
    addContextMenuTool(commandName, ToolClass, options = null) {
        if (options === null) {
            options = {
                toolGroup: 'context-menu-primary'
            }
        }

        super.addTool(commandName, ToolClass, options)
    }

    /**
     * Adds a tool that will displayed in overlay/annotations
     *
     * @param {string} commandName - Name of the Command to execute
     * @param {class} ToolClass - Class which inherits from Substance's Tool class
     */
    addOverlayTool(commandName, ToolClass) {
        const options = {
            toolGroup: 'overlay'
        }

        super.addTool(commandName, ToolClass, options)
    }

    /**
     * Add validator class to list of validators run when saving an article
     *
     * @param {class} Validator
     */
    addValidator(Validator) {
        this.validatorRegistry.addValidator(Validator)
    }

    /**
     * Return an array with all registered validators
     *
     * @return {array}
     */
    getValidators() {
        return this.validatorRegistry.getValidators()
    }

    /**
     * Register a class with an execute method as a hook. All hook classes
     * must implement an execute() method which returns a Promise.
     *
     * Hook types
     *  1. before:validate, Before validation, can change NewsML through api calls.
     *  2. before:save, Before save, but after validation has passed. Changing NewsML not allowed.
     *  3. after:save, After save has succeeded.
     *
     * @example
     * class MyHook {
     *    execute() {
     *       return new Promise((resolve, reject) => {
     *          /// Do Something
     *          resolve()
     *       })
     *    }
     * }
     * import {hook} from 'writer'
     * export default {
     *    name: 'myplugin',
     *    id: 'se.infomaker.myplugin',
     *    configure: function (config) {
     *       config.addHook(hook.AFTER_SAVE, NotifyComponent);
     *    }
     * }
     *
     *
     * @param {string} type Any hook type defined in {hook} from 'writer'
     * @param {class} hookClass Class implementing the hook execute method
     */
    addHook(type, hookClass) {
        this.hookRegistry.addHook(type, hookClass)
    }

    /**
     * Get all registered hooks by type
     *
     * @param {string} type
     *
     * @returns {array<*>}
     */
    getHooks(type) {
        return this.hookRegistry.getHooks(type)
    }

    /**
     * Overloaded method with default value for options
     * should make intellisense happier.
     *
     * @param pkg
     * @param options
     *
     * @returns {configurator}
     */
    import(pkg, options = {}) {
        return super.import(pkg, options)
    }

    /**
     * Add packages that should be imported last
     *
     * @param  {Object} pkg          [description]
     * @param  {Object} [options={}] [description]
     */
    importAfter(pkg, options = {}) {
        this._packagesToImportAfter.push({
            pkg: pkg,
            options: options
        })
    }

    /**
     * Import all packages that should be imported after the normal
     * imports have been done.
     * Not "really" private, but internal to the Writer core.
     *
     * @private
     */
    importAfterPackages() {
        this._packagesToImportAfter.forEach(item => {
            this.import(item.pkg, item.options || {})
        })
    }

    /**
     * Add component to component registry
     *
     * @param {string} name - Public registered name of component
     * @param {Class} ComponentClass - Component subclass
     * @param {boolean} [force=false] - Optionally override previous registration
     * @param {string}  [deprecatedName=null] - Optionally register deprecated name
     */
    addComponent(name, ComponentClass, force = false, deprecatedName = null) {
        if (deprecatedName) {
            this.deprecatedComponentsMap[deprecatedName] = name
        }

        super.addComponent(name, ComponentClass, force)
    }

    /**
     * Get a component by name
     *
     * @param  {string} name Name of component
     * @return {Component} Subclass of Component
     */
    getComponent(name) {
        let componentName = name

        if (this.deprecatedComponentsMap[name]) {
            componentName = this.deprecatedComponentsMap[name]
            console.warn(`Component "${name}" is deprecated, use "${componentName}"`)
        }

        return this.getComponentRegistry().entries[componentName]
    }

    /**
     * Fetch componentRegistry.
     *
     * Overrides Configurator.getComponentRegistry() to make sure it uses
     * deprecated name mapping in order to fetch components using deprecated
     * names and output warning.
     *
     * @return {ComponentRegistry}
     */
    getComponentRegistry() {
        const cr = super.getComponentRegistry()

        // Override this objects get method
        cr.get = (name) => {
            return this.getComponent(name)
        }

        return cr
    }

    /**
     * Register a conflict resolver in the writer.
     * The conflict resolver will be called when the server responds with <pre>409 Conflict</pre>
     * when an article is saved.
     * @param reason
     * @param resolverClass The resolver class
     */
    registerConflictHandler(reason, resolverClass) {
        this.conflictHandlerRegistry.set(reason, resolverClass)
    }

    getConflictHandler(reason) {
        return this.conflictHandlerRegistry.get(reason)
    }

    /**
     * Add fileproxy to handle binaries of a certain type
     *
     * @param {class} FileProxyClass
     */
    addFileProxy(FileProxyClass) {
        if (this.config.fileProxies.some((fileProxy) => fileProxy.name === FileProxyClass.name)) {
            return
        }

        super.addFileProxy(FileProxyClass)
    }
}

export {DWConfigurator}
